import { useReportsApi } from '@/src/hooks/api/useReportsApi';
import { Button, Header, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../../config/app-config';
import { useEvents } from '../../../../store/events.context';
import { Nullable } from '../../../../types/common';
import { DetailedEventStatisticsReport, TeamMetricsRow } from '../../../../types/DetailedEventStatisticsReport';
import { downloadObjectAsCsv, downloadObjectAsJson } from '../../../../utils/download.utils';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { HorizontalRule } from '../../../common/HorizontalRule';
import ChallengeMetrics from './ChallengeMetrics';
import { PARReport } from './PARReport';
import TeamMetricDetails from './TeamMetrics/TeamMetricDetails';
import TeamMetricsTable from './TeamMetrics/TeamMetricsTable';

interface ReportProps {
  eventName: string;
}

export const EventReport: React.FC<ReportProps> = ({ eventName }) => {
  const { t } = useTranslation();
  const { event } = useEvents();
  const [eventReport, setEventReport] = useState<DetailedEventStatisticsReport>();
  const [selectedTeam, setSelectedTeam] = useState<Nullable<TeamMetricsRow>>(null);
  const [activeTab, setActiveTab] = useState('challenge-metrics');
  const [loading, setLoading] = useState(false);
  const reportsApi = useReportsApi();

  const loadEventReport = async () => {
    setLoading(true);
    try {
      const newEventReport = await reportsApi.getEventReport(eventName);

      setEventReport(newEventReport);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEventReport();
  }, [eventName]);

  const downloadAsJson = () => {
    if (eventReport) {
      const eventJson = JSON.stringify(eventReport, null, 2);
      downloadObjectAsJson(eventJson, (eventName || 'jam') + '-event-report');
    }
  };

  const downloadChallengeMetrics = () => {
    if (eventReport && eventReport.challengeMetrics) {
      const challengeReport = eventReport.challengeMetrics;
      downloadObjectAsCsv(challengeReport, (eventName || 'jam') + '-challengeMetrics');
    }
  };

  const downloadTeamMetrics = () => {
    if (eventReport && eventReport.teamMetrics) {
      const challengeReport = eventReport.teamMetrics;
      downloadObjectAsCsv(challengeReport, (eventName || 'jam') + '-teamMetrics');
    }
  };

  if (loading) {
    return (
      <SpaceBetween direction="vertical" alignItems="center" size="s">
        <Spinner size="large" />
      </SpaceBetween>
    );
  }

  return (
    <React.Fragment>
      {!selectedTeam && (
        <React.Fragment>
          <Header
            variant="h2"
            actions={
              <SpaceBetween size={'s'} direction="horizontal">
                <Button variant="normal" onClick={() => downloadAsJson()}>
                  {t(i18nKeys.report.buttons.downloadReport)} (JSON)
                </Button>
                <Button variant="normal" onClick={() => downloadChallengeMetrics()}>
                  {t(i18nKeys.report.buttons.exportChallengeMetrics)}
                </Button>
                <Button variant="normal" onClick={() => downloadTeamMetrics()}>
                  {t(i18nKeys.report.buttons.exportTeamMetrics)}
                </Button>
              </SpaceBetween>
            }
          >
            {t(i18nKeys.report.headers.report)}
          </Header>
          <HorizontalRule evenMargins className="zero-margin-bottom" />
          <Tabs
            tabs={[
              {
                id: 'challenge-metrics',
                label: t(i18nKeys.report.headers.challengeMetrics),
                content: (
                  <div>
                    {eventReport?.challengeMetrics.map((challengeMetrics, i) => {
                      if (challengeMetrics && challengeMetrics.challengeId) {
                        return (
                          <ChallengeMetrics
                            key={`challenge-metrics-row-${i}`}
                            challengeMetricsRow={challengeMetrics}
                            teamCount={eventReport.teamMetrics.length || 0}
                            title={eventReport.challengeTitles[challengeMetrics.challengeId]}
                          />
                        );
                      }
                    })}
                  </div>
                ),
              },
              {
                id: 'team-metrics',
                label: t(i18nKeys.report.headers.teamMetrics),
                content: (
                  <div>
                    {eventReport?.teamMetrics && (
                      <TeamMetricsTable teamMetrics={eventReport?.teamMetrics} selectTeam={setSelectedTeam} />
                    )}
                  </div>
                ),
              },
              ...(config.ff.enablePARReport && event?.ended
                ? [
                    {
                      id: 'par-report',
                      label: t(i18nKeys.report.parReport.title),
                      content: (
                        <div>
                          <PARReport eventName={eventName} />
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
            activeTabId={activeTab}
            onChange={(tabChangeEvent) => setActiveTab(tabChangeEvent.detail.activeTabId)}
          />
        </React.Fragment>
      )}
      {!!selectedTeam && <TeamMetricDetails teamMetrics={selectedTeam} selectTeam={setSelectedTeam} />}
    </React.Fragment>
  );
};
